import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { StatusBadge } from "@/components/common/StatusBadge";
import { AsyncState, EmptyState } from "@/components/common/Loader";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { CONTENT_SCHEMAS } from "@/config/contentSchemas";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { ManageFiltersPanel, ManagePackageFiltersPanel, ManageStepsPanel } from "@/pages/admin/AdminInlineManagers";

export default function AdminContentList() {
  const { type } = useParams();
  const navigate = useNavigate();
  const schema = CONTENT_SCHEMAS[type];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/${type}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [type]);
  useEffect(() => { if (schema) load(); }, [schema, load]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      const title = it[schema?.primary] || it.title || "";
      const matchesSearch = title.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === "all" || it.computed_status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [items, search, statusFilter, schema]);

  const remove = async (id) => {
    try { await api.delete(`/admin/${type}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  if (!schema) return <AdminLayout><EmptyState title="Unknown content type" /></AdminLayout>;

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="display-title text-4xl">{schema.label}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{items.length} item(s)</p>
        </div>
        <Link to={`/admin/${type}/new`} data-testid="admin-crud-new-button" className="inline-flex items-center gap-2 rounded-md bg-brand px-5 py-2.5 text-sm font-semibold uppercase tracking-wide text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors"><Plus className="h-4 w-4" /> New {schema.singular}</Link>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-crud-search-input" className="max-w-xs" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="admin-crud-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Draft">Draft</SelectItem>
            <SelectItem value="Scheduled">Scheduled</SelectItem>
            <SelectItem value="Expired">Expired</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-border bg-card">
        <AsyncState loading={loading} empty={filtered.length === 0} emptyTitle="No items" emptyDescription="Create your first item to get started.">
          <table className="w-full text-sm" data-testid="admin-crud-table">
            <thead className="border-b border-border bg-muted/40">
              <tr className="text-left">
                <th className="px-4 py-3 font-condensed font-semibold uppercase tracking-wide">Title</th>
                <th className="px-4 py-3 font-condensed font-semibold uppercase tracking-wide">Status</th>
                <th className="hidden px-4 py-3 font-condensed font-semibold uppercase tracking-wide sm:table-cell">Order</th>
                <th className="px-4 py-3 text-right font-condensed font-semibold uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-3 font-medium">{it[schema.primary] || it.title || "Untitled"}</td>
                  <td className="px-4 py-3"><StatusBadge status={it.computed_status} /></td>
                  <td className="hidden px-4 py-3 text-muted-foreground sm:table-cell">{it.order ?? 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => navigate(`/admin/${type}/${it.id}`)} data-testid={`admin-edit-${it.id}`} className="flex h-9 w-9 items-center justify-center rounded-md border border-border hover:bg-accent transition-colors" title="Edit"><Pencil className="h-4 w-4" /></button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid={`admin-delete-${it.id}`} className="flex h-9 w-9 items-center justify-center rounded-md border border-border text-destructive hover:bg-destructive/10 transition-colors" title="Delete"><Trash2 className="h-4 w-4" /></button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete this item?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(it.id)} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </AsyncState>
      </div>

      {type === "testimonials" && <ManageFiltersPanel />}
      {type === "packages" && <ManagePackageFiltersPanel />}
      {type === "volleyball_resources" && <ManageStepsPanel />}
    </AdminLayout>
  );
}
